import { useTranslation } from 'react-i18next'
import { mdiEmailFastOutline } from '@mdi/js'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { useOwnSetting } from '../../hooks/useOwnSetting'
import { DEFAULT_DIGEST, EMAIL_DIGEST_SETTING, type DigestPreference } from '../../lib/digest'

/**
 * The one-time offer of the daily digest (spec 2026-10-27-23), at the top of
 * Pilotage, for a member who never chose: the digest is opt-in, so the offer
 * has to be where the badges are. « Activer » turns it on at 07:00 on working
 * days; « Plus tard » stores `{enabled: false}`, which hides the card for
 * good. The « Notifications » tab stays the place to change it.
 */
export function DigestInvite({ org }: { org: string }) {
    const { t } = useTranslation('users')
    const digest = useOwnSetting<Partial<DigestPreference>>(org, EMAIL_DIGEST_SETTING)
    if (digest.isLoading || digest.isError || digest.present) return null

    return (
        <div
            className="mb-4 flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-center"
            data-testid="digest-invite"
        >
            <AppIcon path={mdiEmailFastOutline} size="md" className="text-primary shrink-0" />
            <div className="flex-1 text-sm">
                <p className="font-medium">{t('notifications.invite_title')}</p>
                <p className="text-muted-foreground">{t('notifications.invite_body')}</p>
                {digest.saveError && (
                    <p className="text-destructive" data-testid="digest-invite-error">{t('notifications.save_error')}</p>
                )}
            </div>
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    onClick={() => void digest.save({ enabled: false })}
                    disabled={digest.isSaving}
                    data-testid="digest-invite-dismiss"
                >
                    {t('notifications.invite_later')}
                </Button>
                <Button
                    onClick={() => void digest.save({ ...DEFAULT_DIGEST, enabled: true })}
                    disabled={digest.isSaving}
                    data-testid="digest-invite-enable"
                >
                    {t('notifications.invite_enable')}
                </Button>
            </div>
        </div>
    )
}
